import Phaser from "phaser";

/**
 * LimeZu Character Generator atlas — Phaser asset registry for the layered
 * sprite avatar system that replaces the procedural drawing in Phase 11.
 *
 * Each Character_Generator part PNG is a 1792×1312 px spritesheet with a
 * fixed grid layout (32×64 frames; 56 cols × 20 rows). Layers are stacked
 * at runtime in z-order:
 *
 *   body  →  eyes  →  outfit  →  hair  →  headgear  →  accessory
 *
 * Because all part sheets share the same grid, drawing the same frame
 * index from each layered sprite gives a synchronized character pose.
 *
 * MVP strategy:
 *   - Frame 3 (col 3, row 0 of preview row) is a known-good front-facing
 *     "idle down" pose. We use that as the static default.
 *   - Animations (idle/walk/sit/phone) are wired in `anims.ts` once we
 *     decode the per-row offsets. For now setAnim('idle-down') uses
 *     frame 3 as a single static frame.
 *
 * The atlas lazy-loads parts on demand to keep memory bounded — only the
 * parts the local player + visible remote players are using get loaded
 * into the GPU. CharacterCreator preloads on tab click.
 */

export const FRAME_WIDTH = 32;
export const FRAME_HEIGHT = 64;
// Body atlas is 1854×1312 → 57 columns of 32-px frames (verified at runtime;
// the file-format comment elsewhere claiming 56 was a guess that didn't survive
// inspection). Off-by-one here puts the walk-row frames in the wrong row.
export const FRAMES_PER_ROW = 57;

/** Verified working static frame: front-facing preview ("idle-down"). */
export const PREVIEW_FRAME_DOWN = 3;
export const PREVIEW_FRAME_RIGHT = 0;
export const PREVIEW_FRAME_UP = 1;
export const PREVIEW_FRAME_LEFT = 2;

export type Direction = "down" | "up" | "left" | "right";

export const DIRECTION_FRAMES: Record<Direction, number> = {
  down: PREVIEW_FRAME_DOWN,
  up: PREVIEW_FRAME_UP,
  left: PREVIEW_FRAME_LEFT,
  right: PREVIEW_FRAME_RIGHT,
};

/**
 * LimeZu animation row layout (per Spritesheet_animations_GUIDE.png):
 *   Row 0 (frames 0-55):     premade previews + filler
 *   Row 1 (frames 56-111):   IDLE — 14 frames per direction × 4 directions
 *   Row 2 (frames 112-167):  WALK — same per-direction layout
 *
 * Within an animation row, the directional segments run in this order:
 *   right, up, left, down — matching PREVIEW_FRAME_RIGHT/UP/LEFT/DOWN (0/1/2/3).
 *
 * For walk we use 6 frames per cycle (the first 6 of each 14-frame block) at
 * 8 fps — enough to read as legs moving without burning the whole row.
 */
export const ROW_IDLE = 1;
export const ROW_WALK = 2;
export const FRAMES_PER_DIRECTION = 14;

const DIRECTION_ORDER: Direction[] = ["right", "up", "left", "down"];

export function walkFrames(direction: Direction): number[] {
  const dirIdx = DIRECTION_ORDER.indexOf(direction);
  const startFrame = ROW_WALK * FRAMES_PER_ROW + dirIdx * FRAMES_PER_DIRECTION;
  const frames: number[] = [];
  for (let i = 0; i < 6; i++) frames.push(startFrame + i);
  return frames;
}

export function idleFrames(direction: Direction): number[] {
  const dirIdx = DIRECTION_ORDER.indexOf(direction);
  const startFrame = ROW_IDLE * FRAMES_PER_ROW + dirIdx * FRAMES_PER_DIRECTION;
  // Idle: 4-frame subtle bob loop.
  return [startFrame, startFrame + 1, startFrame + 2, startFrame + 1];
}

export type Layer =
  | "body"
  | "eyes"
  | "outfit"
  | "hair"
  | "headgear"
  | "accessory";

/** Manifest entry shape (mirrors manifest.json). */
interface ManifestEntry {
  family: number;
  variant?: number;
  path: string;
  w: number;
  h: number;
  label?: string;
}

interface CGenManifest {
  bodies: ManifestEntry[];
  eyes: ManifestEntry[];
  hairstyles: ManifestEntry[];
  outfits: ManifestEntry[];
  accessories: ManifestEntry[];
  smartphones: ManifestEntry[];
  books: ManifestEntry[];
  premade: { idx: number; path: string }[];
}

interface Manifest {
  packs: {
    character_generator: CGenManifest;
    headgear: ManifestEntry[];
    swords: { name: string; path: string }[];
    npcs_legacy: Record<string, Record<string, string>>;
  };
}

let manifest: Manifest | null = null;
let manifestPromise: Promise<Manifest> | null = null;

export async function loadManifest(): Promise<Manifest> {
  if (manifest) return manifest;
  if (!manifestPromise) {
    manifestPromise = fetch("/art/_meta/manifest.json")
      .then((r) => r.json())
      .then((m: Manifest) => {
        manifest = m;
        return m;
      });
  }
  return manifestPromise;
}

export function getManifest(): Manifest {
  if (!manifest) throw new Error("manifest not loaded — call loadManifest() first");
  return manifest;
}

/** Atlas key generator — every spritesheet loaded into Phaser uses these keys. */
export function partKey(layer: Layer, family: number, variant: number = 0): string {
  if (variant > 0) return `limezu:${layer}:${family.toString().padStart(2, "0")}:${variant.toString().padStart(2, "0")}`;
  return `limezu:${layer}:${family.toString().padStart(2, "0")}`;
}

export function thumbUrl(layer: Layer | "premade", family: number, variant: number = 0): string {
  if (variant > 0) return `/art/_meta/avatar-thumbs/${layer}/${family.toString().padStart(2, "0")}-${variant.toString().padStart(2, "0")}.png`;
  return `/art/_meta/avatar-thumbs/${layer}/${family.toString().padStart(2, "0")}.png`;
}

/** Resolve the manifest entry for a given (layer, family, variant). */
function findEntry(layer: Layer, family: number, variant: number): ManifestEntry | null {
  const m = getManifest().packs.character_generator;
  const lookup: Record<Layer, ManifestEntry[]> = {
    body: m.bodies,
    eyes: m.eyes,
    outfit: m.outfits,
    hair: m.hairstyles,
    headgear: getManifest().packs.headgear,
    accessory: m.accessories,
  };
  const list = lookup[layer];
  if (!list) return null;
  if (layer === "body" || layer === "eyes" || layer === "headgear" /* family-only or family+variant */) {
    if (layer === "headgear") {
      return list.find((e) => e.family === family && (e.variant ?? 0) === variant) ?? null;
    }
    return list.find((e) => e.family === family) ?? null;
  }
  return list.find((e) => e.family === family && e.variant === variant) ?? null;
}

const loadedKeys = new Set<string>();

/**
 * Queue a Phaser load for one part variant. Idempotent — won't double-load.
 * The actual texture only becomes available once `scene.load.start()` finishes
 * (or if called during preload).
 */
export function queueLoadPart(
  scene: Phaser.Scene,
  layer: Layer,
  family: number,
  variant: number = 0,
): string | null {
  const entry = findEntry(layer, family, variant);
  if (!entry) {
    console.warn(`[atlas] no manifest entry for ${layer}:${family}:${variant}`);
    return null;
  }
  const key = partKey(layer, family, variant);
  if (loadedKeys.has(key) || scene.textures.exists(key)) return key;
  // Headgear is a 32×32 single-frame image, not a 1792×1312 spritesheet.
  if (layer === "headgear") {
    scene.load.image(key, entry.path);
  } else {
    scene.load.spritesheet(key, entry.path, {
      frameWidth: FRAME_WIDTH,
      frameHeight: FRAME_HEIGHT,
    });
  }
  loadedKeys.add(key);
  return key;
}

/**
 * Force-load a list of parts and resolve when all textures are present.
 * Used by CharacterCreator + MainScene to hot-load on demand.
 *
 * Phaser's loader requires careful handling when called outside preload(): we
 * queue the new files, ensure the loader is started, and listen for the
 * "complete" event (or per-file "filecomplete-spritesheet-<key>" events).
 */
export function loadParts(
  scene: Phaser.Scene,
  parts: Array<{ layer: Layer; family: number; variant?: number }>,
): Promise<void> {
  return new Promise((resolve) => {
    const targetKeys: string[] = [];
    for (const p of parts) {
      const key = queueLoadPart(scene, p.layer, p.family, p.variant ?? 0);
      if (key && !scene.textures.exists(key)) targetKeys.push(key);
    }
    if (targetKeys.length === 0) {
      resolve();
      return;
    }

    // Resolve when every target key has its texture available, regardless of
    // whether loader emits "complete" (it may already have started for other files).
    let resolved = false;
    const checkDone = () => {
      if (resolved) return;
      if (targetKeys.every((k) => scene.textures.exists(k))) {
        resolved = true;
        resolve();
      }
    };

    // Per-file listener: fires when an individual texture finishes loading.
    for (const k of targetKeys) {
      // For spritesheet load events
      scene.load.once(`filecomplete-spritesheet-${k}`, checkDone);
      // For image load events (headgear)
      scene.load.once(`filecomplete-image-${k}`, checkDone);
    }
    scene.load.once("complete", checkDone);

    if (!scene.load.isLoading()) {
      scene.load.start();
    }

    // Safety net — poll briefly in case the loader was already past these
    // files by the time we set up listeners.
    let polls = 0;
    const poll = scene.time.addEvent({
      delay: 100,
      loop: true,
      callback: () => {
        polls++;
        checkDone();
        if (resolved || polls > 60) poll.destroy();
      },
    });
  });
}

/**
 * Preload the default avatar parts during a scene's preload() phase.
 * Guarantees that body 01 + eyes 01 + outfit 01:01 + hair 01:01 textures
 * are available the moment the scene transitions to create(), so the local
 * + remote avatars never render with missing textures.
 *
 * Manifest must already be loaded; this expects fetch to resolve synchronously
 * from sessionStorage cache or a prior call to loadManifest().
 */
export function preloadLimezuDefaults(scene: Phaser.Scene): void {
  // We can't await the manifest in preload(), so use direct manifest paths
  // for the canonical defaults. These four files MUST exist on disk.
  const FALLBACKS: Array<{ key: string; path: string; isImage?: boolean }> = [
    { key: "limezu:body:01",       path: "/art/limezu/2_Characters/Character_Generator/Bodies/32x32/Body_32x32_01.png" },
    { key: "limezu:eyes:01",       path: "/art/limezu/2_Characters/Character_Generator/Eyes/32x32/Eyes_32x32_01.png" },
    { key: "limezu:outfit:01:01",  path: "/art/limezu/2_Characters/Character_Generator/Outfits/32x32/Outfit_01_32x32_01.png" },
    { key: "limezu:hair:01:01",    path: "/art/limezu/2_Characters/Character_Generator/Hairstyles/32x32/Hairstyle_01_32x32_01.png" },
  ];
  for (const f of FALLBACKS) {
    if (scene.textures.exists(f.key)) continue;
    if (f.isImage) {
      scene.load.image(f.key, f.path);
    } else {
      scene.load.spritesheet(f.key, f.path, {
        frameWidth: FRAME_WIDTH,
        frameHeight: FRAME_HEIGHT,
      });
    }
    loadedKeys.add(f.key);
  }
}
